import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FiPlay, FiTv, FiArrowLeft, FiClock, FiCalendar, FiHeart, FiShare2, FiChevronDown } from 'react-icons/fi';
import './TVShow.css';

const TVShow = () => {
  const { showSlug } = useParams();
  const navigate = useNavigate();
  const [show, setShow] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(1);
  const [allEpisodes, setAllEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    loadShowDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSlug]);

  useEffect(() => {
    // If user landed on a numeric identifier, replace it with the canonical slug.
    if (!show?.slug) return;
    if (showSlug === show.slug) return;
    if (!/^\d+$/.test(showSlug)) return;
    navigate(`/tv/show/${show.slug}`, { replace: true });
  }, [showSlug, show?.slug, navigate]);

  const loadShowDetails = async () => {
    try {
      const res = await fetch(`http://localhost:5000/api/tv/show/${showSlug}`);
      const data = await res.json();
      setShow(data.show);
      const seasonsList = (data.seasonsList || []).map(s => ({
        season_number: Number(s.season_number),
        episodes_count: Number(s.episodes_count)
      }));
      setSeasons(seasonsList);
      setAllEpisodes((data.episodes || []).map(ep => ({
        ...ep,
        season_number: Number(ep.season_number),
        episode_number: Number(ep.episode_number)
      })));

      const firstSeason = seasonsList[0]?.season_number || 1;
      setSelectedSeason(firstSeason);
    } catch (error) {
      console.error('Error loading show:', error);
      setShow(null);
      setSeasons([]);
      setAllEpisodes([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (rawDuration) => {
    if (rawDuration === null || rawDuration === undefined) return '';
    if (typeof rawDuration === 'string') return rawDuration;
    const minutes = Number(rawDuration);
    if (Number.isNaN(minutes)) return '';
    if (minutes < 60) return `${minutes} min`;
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hrs}h ${mins}min`;
  };

  const seasonEpisodes = allEpisodes
    .filter(ep => ep.season_number === selectedSeason)
    .sort((a, b) => a.episode_number - b.episode_number);

  const slugify = (text) => {
    if (!text) return '';
    return text
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const getShowSlug = () => show?.slug || showSlug;

  const getEpisodeSlug = (ep) => {
    if (ep?.slug) return ep.slug;
    const base = slugify(ep?.title);
    const prefix = `t${ep.season_number}-e${ep.episode_number}`;
    return base ? `${prefix}-${base}` : prefix;
  };

  if (loading) {
    return (
      <div className="tvshow-loading">
        <div className="loading-spinner">
          <FiTv />
        </div>
        <p>Cargando programa...</p>
      </div>
    );
  }

  if (!show) {
    return (
      <div className="tvshow-not-found">
        <h2>Programa no encontrado</h2>
        <Link to="/tv" className="back-link">
          <FiArrowLeft /> Volver a TV
        </Link>
      </div>
    );
  }

  const firstEpisode = [...allEpisodes].sort((a, b) => {
    if (a.season_number !== b.season_number) return a.season_number - b.season_number;
    return a.episode_number - b.episode_number;
  })[0];

  return (
    <div className="tvshow-page">
      {/* Hero Banner */}
      <section className="tvshow-hero">
        <div className="hero-background">
          <img src={show.banner_image || show.cover_image} alt={show.title} />
          <div className="hero-gradient"></div>
        </div>

        <Link to="/tv" className="back-button">
          <FiArrowLeft /> Volver
        </Link>

        <div className="hero-content">
          <div className="show-badge">
            <FiTv /> GRAPHYRA TV
          </div>
          <h1>{show.title}</h1>
          
          <div className="show-meta">
            {show.year && <span className="meta-item"><FiCalendar /> {show.year}</span>}
            <span className="meta-item">{show.total_seasons} {show.total_seasons === 1 ? 'temporada' : 'temporadas'}</span>
            {show.rating && <span className="meta-item rating">{show.rating}</span>}
            {show.duration_avg && <span className="meta-item"><FiClock /> {show.duration_avg}</span>}
          </div>

          <p className="show-description">{show.description}</p>

          <div className="hero-actions">
            {firstEpisode ? (
              <Link to={`/tv/watch/${getShowSlug()}/${getEpisodeSlug(firstEpisode)}`} className="btn-play">
                <FiPlay /> Reproducir
              </Link>
            ) : (
              <Link to="/tv" className="btn-play">
                <FiPlay /> Volver a TV
              </Link>
            )}
            <button 
              className={`btn-favorite ${isFavorite ? 'active' : ''}`}
              onClick={() => setIsFavorite(!isFavorite)}
            >
              <FiHeart /> {isFavorite ? 'En favoritos' : 'Añadir a favoritos'}
            </button>
            <button className="btn-share">
              <FiShare2 />
            </button>
          </div>
        </div>
      </section>

      {/* Selector de temporada y episodios */}
      <section className="episodes-section">
        <div className="section-header">
          <div className="season-selector">
            <select 
              value={selectedSeason} 
              onChange={(e) => setSelectedSeason(Number(e.target.value))}
            >
              {seasons.map(season => (
                <option key={season.season_number} value={season.season_number}>
                  Temporada {season.season_number}
                </option>
              ))}
            </select>
            <FiChevronDown className="select-icon" />
          </div>
          <span className="episodes-count">{seasonEpisodes.length} episodios</span>
        </div>

        <div className="episodes-list">
          {seasonEpisodes.map(episode => (
            <Link 
              key={episode.id}
              to={`/tv/watch/${getShowSlug()}/${getEpisodeSlug(episode)}`}
              className="episode-card"
            >
              <div className="episode-thumbnail">
                <img src={episode.thumbnail || show.cover_image} alt={episode.title} />
                <div className="episode-play">
                  <FiPlay />
                </div>
                <span className="episode-duration">{formatDuration(episode.duration)}</span>
              </div>
              <div className="episode-info">
                <div className="episode-number">Episodio {episode.episode_number}</div>
                <h3>{episode.title}</h3>
                <p>{episode.description}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

export default TVShow;
